import {
  ArchiveBoxIcon,
  BanknotesIcon,
  ChatBubbleLeftIcon,
  CheckIcon,
  DocumentTextIcon,
  HandThumbUpIcon,
  StopIcon,
  TruckIcon,
  WrenchIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React from "react";

import {statusColors} from "../../../theme/statusPalette";
import {WorkorderStatus} from "../../../types/workorder/workorderStatus";

type StatusFamily = keyof typeof statusColors;

export interface WorkorderStatusStyle {
  /** <famille>-100 : fond des badges (tableau + filtres statut) */
  soft: string;
  softClass: string;
  /** <famille>-400 : croix des filtres statut */
  mid: string;
  /** <famille>-500 : fond des icônes et boutons */
  solid: string;
  solidClass: string;
  /** <famille>-800 : texte posé sur `soft` */
  text: string;
  textClass: string;
  icon: React.ElementType;
}

const CLASSES: Record<StatusFamily, { soft: string; solid: string; text: string }> = {
  "brilliant-rose": {
    soft: "bg-brilliant-rose-100",
    solid: "bg-brilliant-rose-500",
    text: "text-brilliant-rose-800",
  },
  "blue-chronoflex": {
    soft: "bg-blue-chronoflex-100",
    solid: "bg-blue-chronoflex-500",
    text: "text-blue-chronoflex-800",
  },
  "international-orange": {
    soft: "bg-international-orange-100",
    solid: "bg-international-orange-500",
    text: "text-international-orange-800",
  },
  "pastel-green": {
    soft: "bg-pastel-green-100",
    solid: "bg-pastel-green-500",
    text: "text-pastel-green-800",
  },
  "purple-heart": {
    soft: "bg-purple-heart-100",
    solid: "bg-purple-heart-500",
    text: "text-purple-heart-800",
  },
  "navy-blue": {
    soft: "bg-navy-blue-100",
    solid: "bg-navy-blue-500",
    text: "text-navy-blue-800",
  },
  eden: {
    soft: "bg-eden-100",
    solid: "bg-eden-500",
    text: "text-eden-800",
  },
  yellow: {
    soft: "bg-yellow-100",
    solid: "bg-yellow-500",
    text: "text-yellow-800",
  },
  red: {
    soft: "bg-red-100",
    solid: "bg-red-500",
    text: "text-red-800",
  },
  "chrono-gray": {
    soft: "bg-chrono-gray-100",
    solid: "bg-chrono-gray-500",
    text: "text-chrono-gray-800",
  },
};

const style = (family: StatusFamily, icon: React.ElementType): WorkorderStatusStyle => ({
  soft: statusColors[family][100],
  softClass: CLASSES[family].soft,
  mid: statusColors[family][400],
  solid: statusColors[family][500],
  solidClass: CLASSES[family].solid,
  text: statusColors[family][800],
  textClass: CLASSES[family].text,
  icon,
});

const STYLES: Record<string, WorkorderStatusStyle> = {
  [WorkorderStatus.CREATED]: style("brilliant-rose", ChatBubbleLeftIcon),
  [WorkorderStatus.ACCEPTED]: style("yellow", CheckIcon),
  [WorkorderStatus.IN_TRANSIT]: style("blue-chronoflex", TruckIcon),
  [WorkorderStatus.IN_PROGRESS]: style("international-orange", WrenchIcon),
  [WorkorderStatus.SUSPENDED]: style("red", StopIcon),
  [WorkorderStatus.CANCELED]: style("purple-heart", XMarkIcon),
  [WorkorderStatus.CLOSED]: style("chrono-gray", ArchiveBoxIcon),
  [WorkorderStatus.VALIDATED]: style("pastel-green", HandThumbUpIcon),
  [WorkorderStatus.INVOICED]: style("navy-blue", DocumentTextIcon),
  [WorkorderStatus.PAID]: style("eden", BanknotesIcon),
};

const FALLBACK = STYLES[WorkorderStatus.CLOSED];

/**
 * getWorkorderStatusStyle: style associé à un statut d'intervention
 * @param status - statut, quelle que soit sa casse
 * @return - style du statut, gris neutre si le statut est inconnu
 */
export const getWorkorderStatusStyle = (status?: string | null): WorkorderStatusStyle => {
  if (!status) return FALLBACK;
  return STYLES[status.toUpperCase()] ?? FALLBACK;
};

export default STYLES;
